import React, { useEffect, useState, useCallback } from "react";
import { API_BASE, fetchWithAuth } from "../utils/api";
import EditableCell from "./EditableCell";
import { useDateScroll } from "../contexts/DateScrollContext";

function RevenueTable({
  rows,
  onRowsChange,
  onRefresh,
  userRole,
  loading = false,
  isLocked = false,
}) {
  const [editingCell, setEditingCell] = useState(null); // {rowIndex, field}
  const [localRows, setLocalRows] = useState([]);
  const [saving, setSaving] = useState({});
  const [insertMenuOpen, setInsertMenuOpen] = useState(null); // rowIndex hoặc idx của hàng
  const [hasRestoredScroll, setHasRestoredScroll] = useState(false);
  const [options, setOptions] = useState({
    laiXe: [],
    soXe: [],
    dieuPhoi: [],
    diemThuc: [],
    chanCong: [],
  });
  const { setScrollToDateFn, setActiveDate, activeDate } = useDateScroll();

  // Hàm scroll tới dòng có ngày tương ứng
  const scrollToDate = useCallback((date) => {
    const firstRowWithDate = localRows.findIndex((row) => row.ngay && row.ngay.trim() === date);
    if (firstRowWithDate !== -1) {
      const rowElement = document.getElementById(`row-${localRows[firstRowWithDate].rowIndex || localRows[firstRowWithDate].tempId}`);
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
        // Highlight tạm thời
        rowElement.classList.add("bg-blue-100");
        setTimeout(() => {
          rowElement.classList.remove("bg-blue-100");
        }, 1000);
      }
    }
  }, [localRows]);

  // Đăng ký scroll function vào context
  useEffect(() => {
    setScrollToDateFn(() => scrollToDate);
    return () => {
      setScrollToDateFn(null);
    };
  }, [scrollToDate, setScrollToDateFn]);

  // Khôi phục scroll position từ localStorage khi component mount (chỉ một lần)
  useEffect(() => {
    if (localRows.length === 0 || hasRestoredScroll) return;

    const savedScrollPosition = localStorage.getItem('revenue-scroll-position');
    const savedActiveDate = localStorage.getItem('revenue-active-date');
    
    if (savedScrollPosition || savedActiveDate) {
      // Đợi một chút để DOM render xong
      setTimeout(() => {
        const mainElement = document.querySelector('main.flex-1');
        const scrollContainer = mainElement?.querySelector('.overflow-y-auto');
        
        if (scrollContainer) {
          // Khôi phục scroll position
          if (savedScrollPosition) {
            scrollContainer.scrollTop = parseInt(savedScrollPosition, 10);
          }
          
          // Khôi phục active date và scroll tới ngày đó
          if (savedActiveDate) {
            setActiveDate(savedActiveDate);
            const firstRowWithDate = localRows.findIndex((row) => row.ngay && row.ngay.trim() === savedActiveDate);
            if (firstRowWithDate !== -1) {
              setTimeout(() => {
                const rowElement = document.getElementById(`row-${localRows[firstRowWithDate].rowIndex || localRows[firstRowWithDate].tempId}`);
                if (rowElement) {
                  rowElement.scrollIntoView({ behavior: 'auto', block: 'center' });
                }
              }, 100);
            }
          }
          
          setHasRestoredScroll(true);
        }
      }, 200);
    } else {
      setHasRestoredScroll(true);
    }
  }, [localRows, setActiveDate, hasRestoredScroll]); // Chỉ chạy một lần khi có dữ liệu

  // Detect ngày đang hiển thị khi scroll và lưu vào localStorage
  useEffect(() => {
    const handleScroll = () => {
      // Tìm container scroll (main content area)
      const mainElement = document.querySelector('main.flex-1');
      const scrollContainer = mainElement?.querySelector('.overflow-y-auto');
      if (!scrollContainer) return;

      // Lưu scroll position vào localStorage
      localStorage.setItem('revenue-scroll-position', scrollContainer.scrollTop.toString());

      const containerRect = scrollContainer.getBoundingClientRect();
      const viewportTop = containerRect.top;
      const viewportBottom = containerRect.bottom;
      const viewportCenter = viewportTop + (viewportBottom - viewportTop) / 2;

      // Tìm row gần nhất với center của viewport
      let closestRow = null;
      let closestDistance = Infinity;

      localRows.forEach((row) => {
        if (!row.ngay || !row.ngay.trim()) return;
        
        const rowElement = document.getElementById(`row-${row.rowIndex || row.tempId}`);
        if (!rowElement) return;

        const rowRect = rowElement.getBoundingClientRect();
        const rowCenter = rowRect.top + (rowRect.bottom - rowRect.top) / 2;
        
        // Kiểm tra xem row có trong viewport không
        if (rowRect.top <= viewportCenter && rowRect.bottom >= viewportCenter) {
          const distance = Math.abs(rowCenter - viewportCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestRow = row;
          }
        }
      });

      // Nếu không tìm thấy row trong viewport center, tìm row đầu tiên trong viewport
      if (!closestRow) {
        localRows.forEach((row) => {
          if (!row.ngay || !row.ngay.trim()) return;
          
          const rowElement = document.getElementById(`row-${row.rowIndex || row.tempId}`);
          if (!rowElement) return;

          const rowRect = rowElement.getBoundingClientRect();
          
          // Kiểm tra xem row có trong viewport không
          if (rowRect.top <= viewportBottom && rowRect.bottom >= viewportTop) {
            if (!closestRow) {
              closestRow = row;
            } else {
              const closestRect = document.getElementById(`row-${closestRow.rowIndex || closestRow.tempId}`)?.getBoundingClientRect();
              if (closestRect && rowRect.top < closestRect.top) {
                closestRow = row;
              }
            }
          }
        });
      }

      if (closestRow && closestRow.ngay) {
        const newActiveDate = closestRow.ngay.trim();
        setActiveDate(newActiveDate);
        // Lưu active date vào localStorage
        localStorage.setItem('revenue-active-date', newActiveDate);
      }
    };

    // Thêm scroll listener với debounce để tối ưu performance
    let scrollTimeout;
    const scrollHandler = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 50);
    };

    const mainElement = document.querySelector('main.flex-1');
    const scrollContainer = mainElement?.querySelector('.overflow-y-auto');
    
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', scrollHandler);
      // Gọi ngay để set active date ban đầu (chỉ nếu chưa có saved state)
      if (!localStorage.getItem('revenue-active-date')) {
        setTimeout(handleScroll, 100);
      }
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', scrollHandler);
      }
      clearTimeout(scrollTimeout);
    };
  }, [localRows, setActiveDate]);

  // Chỉ cho phép Dispatchers và admin chỉnh sửa
  const normalizedRole = userRole?.toLowerCase();
  // Kiểm tra isLocked: có thể là boolean hoặc được tính từ is_locked
  const locked = isLocked === true || isLocked === 1 || isLocked === "1";
  const canEdit =
    (normalizedRole === "dispatchers" || normalizedRole === "admin") && !locked;
  const canEditAccounting =
    (normalizedRole === "accounting" || normalizedRole === "admin") && !locked;
  
  // Nếu tháng bị khóa, không cho phép edit bất kỳ cell nào
  const isReadOnly = locked;

  // Load options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/revenue/options`);
        const data = await res.json();
        if (data.success) {
          setOptions(data.data);
        }
      } catch (err) {
        console.error("Lỗi khi lấy options:", err);
      }
    };
    loadOptions();
  }, []);

  // Đóng menu khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        insertMenuOpen !== null &&
        !event.target.closest(".insert-menu-container")
      ) {
        setInsertMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [insertMenuOpen]);

  // Sync localRows với rows từ props
  useEffect(() => {
    setLocalRows((prev) => {
      // Giữ lại các hàng mới tạm thời (chưa lưu) khi refresh
      const existingNewRows = prev.filter((r) => r.isNew && r.tempId);
      const newRows = [...rows];

      // Thêm hàng mới ở cuối nếu chưa có
      const hasNewRowAtEnd =
        existingNewRows.length > 0 || prev.some((r) => r.isNew && !r.tempId);
      if (!hasNewRowAtEnd) {
        newRows.push({
          rowIndex: null,
          ngay: "", // Để trống, không tự động set ngày
          laiXe: "",
          soXe: "",
          dieuPhoi: "",
          doanhThu: 0,
          diemThuc: "",
          chanCong: 0,
          keToanChuyen: "",
          doiChieuNote: "",
          ungTienKhach150k: "",
          chiPhi: "",
          noteNhap: "",
          isNew: true,
        });
      }

      // Merge với các hàng mới tạm thời
      return [...newRows, ...existingNewRows];
    });
  }, [rows]);


  const handleCellEdit = (rowKey, field) => {
    // Nếu tháng bị khóa, không cho phép edit
    if (isReadOnly) return;
    
    // Dispatchers/admin sửa các cột điều phối; Accounting/admin sửa "keToanChuyen", "ungTienKhach150k", "chiPhi" và "noteNhap"
    if (
      field === "keToanChuyen" ||
      field === "ungTienKhach150k" ||
      field === "chiPhi" ||
      field === "noteNhap"
    ) {
      if (!canEditAccounting) return;
    } else {
      if (!canEdit) return;
    }
    setEditingCell({ rowKey, field });
  };

  const handleCellSave = async (rowKey, field, value) => {
    setEditingCell(null);

    const row = localRows.find((r) => {
      if (r.tempId) return r.tempId === rowKey;
      if (r.isNew && !r.tempId) return rowKey === "new" || rowKey === r.tempId;
      return r.rowIndex === rowKey;
    });

    if (!row) return;

    // Nếu là hàng mới
    if (row.isNew) {
      // Cập nhật local state - giữ lại insertAfterRowIndex và insertPosition
      const updatedRow = {
        ...row,
        [field]: value,
        // Đảm bảo giữ lại thông tin vị trí chèn
        insertAfterRowIndex: row.insertAfterRowIndex,
        insertPosition: row.insertPosition,
      };
      setLocalRows((prev) =>
        prev.map((r) => {
          if (row.tempId && r.tempId === row.tempId) {
            return updatedRow;
          }
          if (r.isNew && !r.tempId && !row.tempId) {
            return updatedRow;
          }
          return r;
        })
      );

      // Hàng mới: KHÔNG tự động lưu. Chỉ cập nhật local state,
      // user sẽ bấm nút "Lưu" ngay trên hàng để tạo row trong sheet.
      return;
    }

    // Cập nhật hàng hiện có
    if (!row.rowIndex) return;

    // So sánh giá trị cũ và mới - nếu giống nhau thì không fetch
    const oldValue = row[field];
    
    // Xử lý so sánh theo từng loại field
    let isValueChanged = false;
    
    if (field === 'doanhThu' || field === 'diemThuc' || field === 'chanCong' || field === 'chiPhi') {
      // So sánh số
      const oldNum = oldValue === null || oldValue === undefined || oldValue === '' ? 0 : Number(oldValue) || 0;
      const newNum = value === null || value === undefined || value === '' ? 0 : Number(value) || 0;
      isValueChanged = oldNum !== newNum;
    } else {
      // So sánh chuỗi
      const normalizedOldValue = oldValue === null || oldValue === undefined ? '' : String(oldValue).trim();
      const normalizedNewValue = value === null || value === undefined ? '' : String(value).trim();
      isValueChanged = normalizedOldValue !== normalizedNewValue;
    }
    
    // Nếu giá trị không thay đổi, không cần fetch
    if (!isValueChanged) {
      return;
    }

    setSaving((prev) => ({ ...prev, [`${row.rowIndex}-${field}`]: true }));
    try {
      if (field === "keToanChuyen") {
        const res = await fetchWithAuth(
          `${API_BASE}/revenue/accounting/${row.rowIndex}`,
          {
            method: "PUT",
            body: JSON.stringify({ keToanChuyen: value }),
          }
        );
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Không thể đối chiếu");
        }

        // Sử dụng giá trị doiChieuNote từ server (được server tự tính: doanhThu - keToanChuyen)
        const doiChieuNote = data.doiChieuNote || "";

        // Cập nhật localRows & rows bên ngoài mà không reload toàn bộ bảng
        setLocalRows((prev) =>
          prev.map((r) =>
            r.rowIndex === row.rowIndex 
              ? { ...r, keToanChuyen: value, doiChieuNote } 
              : r
          )
        );
        if (onRowsChange) {
          onRowsChange((prev) =>
            (prev || []).map((r) =>
              r.rowIndex === row.rowIndex 
                ? { ...r, keToanChuyen: value, doiChieuNote } 
                : r
            )
          );
        }
        return;
      } else if (field === "ungTienKhach150k") {
        const res = await fetchWithAuth(
          `${API_BASE}/revenue/advance/${row.rowIndex}`,
          {
            method: "PUT",
            body: JSON.stringify({ ungTienKhach150k: value }),
          }
        );
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Không thể cập nhật ứng tiền khách");
        }

        setLocalRows((prev) =>
          prev.map((r) =>
            r.rowIndex === row.rowIndex ? { ...r, ungTienKhach150k: value } : r
          )
        );
        if (onRowsChange) {
          onRowsChange((prev) =>
            (prev || []).map((r) =>
              r.rowIndex === row.rowIndex
                ? { ...r, ungTienKhach150k: value }
                : r
            )
          );
        }
        return;
      } else if (field === "chiPhi") {
        const res = await fetchWithAuth(
          `${API_BASE}/revenue/cost/${row.rowIndex}`,
          {
            method: "PUT",
            body: JSON.stringify({ chiPhi: value }),
          }
        );
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Không thể cập nhật chi phí");
        }

        // Cập nhật localRows & rows bên ngoài mà không reload toàn bộ bảng
        setLocalRows((prev) =>
          prev.map((r) =>
            r.rowIndex === row.rowIndex ? { ...r, chiPhi: value } : r
          )
        );
        if (onRowsChange) {
          onRowsChange((prev) =>
            (prev || []).map((r) =>
              r.rowIndex === row.rowIndex ? { ...r, chiPhi: value } : r
            )
          );
        }
        return;
      } else if (field === "noteNhap") {
        const res = await fetchWithAuth(
          `${API_BASE}/revenue/note/${row.rowIndex}`,
          {
            method: "PUT",
            body: JSON.stringify({ noteNhap: value }),
          }
        );
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Không thể cập nhật note");
        }

        // Cập nhật localRows & rows bên ngoài mà không reload toàn bộ bảng
        setLocalRows((prev) =>
          prev.map((r) =>
            r.rowIndex === row.rowIndex ? { ...r, noteNhap: value } : r
          )
        );
        if (onRowsChange) {
          onRowsChange((prev) =>
            (prev || []).map((r) =>
              r.rowIndex === row.rowIndex ? { ...r, noteNhap: value } : r
            )
          );
        }
        return;
      } else {
        // Cập nhật toàn bộ row
        const updatedRow = { ...row, [field]: value };

        // Xử lý doanhThu: nếu rỗng/null/undefined hoặc không phải số hợp lệ thì để rỗng
        let doanhThuValue = "";
        if (
          updatedRow.doanhThu !== undefined &&
          updatedRow.doanhThu !== null &&
          updatedRow.doanhThu !== ""
        ) {
          if (typeof updatedRow.doanhThu === "string") {
            const cleaned = updatedRow.doanhThu
              .toString()
              .replace(/[^\d-]/g, "");
            if (cleaned) {
              const num = Number(cleaned);
              if (!isNaN(num)) {
                doanhThuValue = num;
              }
            }
          } else if (
            typeof updatedRow.doanhThu === "number" &&
            !isNaN(updatedRow.doanhThu)
          ) {
            doanhThuValue = updatedRow.doanhThu;
          }
        }

        const res = await fetchWithAuth(
          `${API_BASE}/revenue/revenue/${row.rowIndex}`,
          {
            method: "PUT",
            body: JSON.stringify({
              ngay: updatedRow.ngay,
              laiXe: updatedRow.laiXe,
              soXe: updatedRow.soXe,
              dieuPhoi: updatedRow.dieuPhoi,
              doanhThu: doanhThuValue, // Có thể rỗng nếu không có giá trị
              diemThuc: updatedRow.diemThuc
                ? typeof updatedRow.diemThuc === "string"
                  ? Number(
                      updatedRow.diemThuc
                        .toString()
                        .replace(/[^\d-]/g, "")
                    ) || 0
                  : updatedRow.diemThuc
                : "",
              chanCong: updatedRow.chanCong
                ? typeof updatedRow.chanCong === "string"
                  ? Number(
                      updatedRow.chanCong.toString().replace(/[^\d-]/g, "")
                    ) || 0
                  : updatedRow.chanCong
                : 0,
            }),
          }
        );
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Không thể cập nhật");
        }

        // Tính lại doiChieuNote nếu server trả về, hoặc tính ở client
        let newDoiChieuNote = data.doiChieuNote;
        if (!newDoiChieuNote && field === 'doanhThu') {
          // Tính lại doiChieuNote ở client nếu server không trả về
          const doanhThuNum = typeof doanhThuValue === 'number' ? doanhThuValue : (doanhThuValue === '' ? 0 : Number(doanhThuValue) || 0);
          const keToanNum = row.keToanChuyen === '' || row.keToanChuyen === null || row.keToanChuyen === undefined
            ? 0
            : (typeof row.keToanChuyen === 'number' ? row.keToanChuyen : Number(row.keToanChuyen) || 0);
          
          const doanhThuEmpty = doanhThuNum === 0 && doanhThuValue === '';
          const keToanEmpty = row.keToanChuyen === '';
          
          if (doanhThuEmpty && keToanEmpty) {
            newDoiChieuNote = '';
          } else if (doanhThuEmpty && !keToanEmpty) {
            // Điều phối chưa nhập nhưng kế toán đã nhập → hiển thị "Trước"
            newDoiChieuNote = `Trước ${keToanNum.toLocaleString('vi-VN')} đ`;
          } else {
            const conNo = doanhThuNum - keToanNum;
            if (conNo > 0) {
              newDoiChieuNote = `Thiếu ${Math.abs(conNo).toLocaleString('vi-VN')} đ`;
            } else if (conNo < 0) {
              newDoiChieuNote = `Thừa ${Math.abs(conNo).toLocaleString('vi-VN')} đ`;
            } else {
              newDoiChieuNote = 'Khớp';
            }
          }
        }

        // Cập nhật localRows & rows bên ngoài mà không reload toàn bộ bảng
        const finalUpdatedRow = {
          ...updatedRow,
          ...(newDoiChieuNote !== undefined ? { doiChieuNote: newDoiChieuNote } : {})
        };
        
        setLocalRows((prev) =>
          prev.map((r) =>
            r.rowIndex === row.rowIndex ? { ...r, ...finalUpdatedRow } : r
          )
        );
        if (onRowsChange) {
          onRowsChange((prev) =>
            (prev || []).map((r) =>
              r.rowIndex === row.rowIndex ? { ...r, ...finalUpdatedRow } : r
            )
          );
        }
      }
    } catch (err) {
      alert(err.message || "Có lỗi xảy ra");
      onRefresh(); // Refresh để lấy lại dữ liệu gốc nếu lỗi
    } finally {
      setSaving((prev) => {
        const newState = { ...prev };
        delete newState[`${row.rowIndex}-${field}`];
        return newState;
      });
    }
  };

  const handleCreateRow = async (rowData) => {
    setSaving((prev) => ({ ...prev, new: true }));
    try {
      // Xử lý doanhThu: nếu rỗng/null/undefined hoặc không phải số hợp lệ thì để rỗng
      let doanhThuValue = "";
      if (
        rowData.doanhThu !== undefined &&
        rowData.doanhThu !== null &&
        rowData.doanhThu !== ""
      ) {
        if (typeof rowData.doanhThu === "string") {
          const cleaned = rowData.doanhThu.toString().replace(/[^\d-]/g, "");
          if (cleaned) {
            const num = Number(cleaned);
            if (!isNaN(num)) {
              doanhThuValue = num;
            }
          }
        } else if (
          typeof rowData.doanhThu === "number" &&
          !isNaN(rowData.doanhThu)
        ) {
          doanhThuValue = rowData.doanhThu;
        }
      }

      const requestBody = {
        ngay: rowData.ngay || "",
        laiXe: rowData.laiXe || "", // Cho phép rỗng
        soXe: rowData.soXe || "", // Cho phép rỗng
        dieuPhoi: rowData.dieuPhoi || "", // Cho phép rỗng
        doanhThu: doanhThuValue, // Có thể rỗng nếu không có giá trị
        diemThuc: rowData.diemThuc
          ? typeof rowData.diemThuc === "string"
            ? Number(rowData.diemThuc.toString().replace(/[^\d-]/g, "")) || 0
            : rowData.diemThuc
          : "",
        chanCong: rowData.chanCong
          ? typeof rowData.chanCong === "string"
            ? Number(rowData.chanCong.toString().replace(/[^\d-]/g, "")) || 0
            : rowData.chanCong
          : 0,
        noteNhap: rowData.noteNhap || "",
      };

      // Nếu có thông tin vị trí chèn, thêm vào request
      if (
        rowData.insertAfterRowIndex !== undefined &&
        rowData.insertAfterRowIndex !== null &&
        rowData.insertPosition
      ) {
        requestBody.insertAfterRowIndex = rowData.insertAfterRowIndex;
        requestBody.insertPosition = rowData.insertPosition; // "above" hoặc "below"
      }

      const res = await fetchWithAuth(`${API_BASE}/revenue/revenue`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || "Không thể tạo doanh thu");
      }
      onRefresh();
    } catch (err) {
      alert(err.message || "Có lỗi xảy ra");
    } finally {
      setSaving((prev) => {
        const newState = { ...prev };
        delete newState.new;
        return newState;
      });
    }
  };

  const handleSaveNewRow = async (row) => {
    // Lưu row mới (kể cả rỗng) theo đúng vị trí (nếu có insertAfterRowIndex/insertPosition)
    await handleCreateRow(row);
  };

  const removeTempRow = (tempId) => {
    setLocalRows((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const handleDeleteRow = async (rowIndex) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa hàng này?")) return;

    setSaving((prev) => ({ ...prev, [`delete-${rowIndex}`]: true }));
    try {
      const res = await fetchWithAuth(`${API_BASE}/revenue/revenue/${rowIndex}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || "Không thể xóa");
      }

      // Xóa cục bộ, không reload toàn bộ bảng
      setLocalRows((prev) => prev.filter((r) => r.rowIndex !== rowIndex));
      if (onRowsChange) {
        onRowsChange((prev) => (prev || []).filter((r) => r.rowIndex !== rowIndex));
      }
    } catch (err) {
      alert(err.message || "Có lỗi xảy ra");
    } finally {
      setSaving((prev) => {
        const newState = { ...prev };
        delete newState[`delete-${rowIndex}`];
        return newState;
      });
    }
  };

  const handleInsertRow = (insertAfterIndex, position) => {
    if (!canEdit) return;

    const targetRow = localRows[insertAfterIndex];
    if (!targetRow) {
      // Nếu không tìm thấy, thêm vào cuối
      const newRow = {
        rowIndex: null,
        ngay: "",
        laiXe: "",
        soXe: "",
        dieuPhoi: "",
        doanhThu: 0,
        diemThuc: "",
        chanCong: 0,
        daChuyen: false,
        keToanChuyen: "",
        doiChieuNote: "",
        noteNhap: "",
        isNew: true,
        tempId: `temp-${Date.now()}-${Math.random()}`,
      };
      setLocalRows((prev) => [...prev, newRow]);
      setTimeout(() => {
        handleCellEdit(newRow.tempId, "ngay");
      }, 100);
      return;
    }

    // Chỉ chèn vào vị trí cụ thể nếu hàng tham chiếu đã có rowIndex (đã lưu trong DB)
    const referenceRowIndex = targetRow.rowIndex;

    const newRow = {
      rowIndex: null,
      ngay: "", // Để trống
      laiXe: "",
      soXe: "",
      dieuPhoi: "",
      doanhThu: 0,
      diemThuc: "",
      chanCong: 0,
      daChuyen: false,
      keToanChuyen: "",
      doiChieuNote: "",
      noteNhap: "",
      isNew: true,
      tempId: `temp-${Date.now()}-${Math.random()}`,
      insertPosition: position, // "above" hoặc "below"
      insertAfterRowIndex: referenceRowIndex,
    };

    setLocalRows((prev) => {
      const actualIndex = prev.findIndex((r) => {
        if (targetRow.tempId) return r.tempId === targetRow.tempId;
        if (targetRow.isNew && !targetRow.tempId) {
          return (
            r.isNew &&
            !r.tempId &&
            r.ngay === targetRow.ngay &&
            r.laiXe === targetRow.laiXe &&
            r.soXe === targetRow.soXe
          );
        }
        return r.rowIndex === targetRow.rowIndex;
      });

      if (actualIndex === -1) {
        return [...prev, newRow];
      }

      const newRows = [...prev];
      if (position === "above") {
        newRows.splice(actualIndex, 0, newRow);
      } else {
        newRows.splice(actualIndex + 1, 0, newRow);
      }
      return newRows;
    });

    setTimeout(() => {
      handleCellEdit(newRow.tempId, "ngay");
    }, 100);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4 md:p-5 mt-4 sm:mt-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 sm:mb-4">
        <h2 className="text-sm sm:text-base font-semibold text-slate-900">
          Bảng doanh thu & đối chiếu kế toán
        </h2>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          {loading ? "Đang tải..." : "Làm mới dữ liệu"}
        </button>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 overflow-x-auto -mx-3 sm:mx-0 border border-slate-100 rounded-lg">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-slate-200 text-xs sm:text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 w-12 sm:w-16 whitespace-nowrap">
                  {canEdit && "Thao tác"}
                </th>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Ngày
                </th>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Lái xe
                </th>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Số xe
                </th>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Điều phối
                </th>
                <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Doanh thu
                </th>
                <th className="px-2 sm:px-3 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Điểm thực
                </th>
                <th className="px-2 sm:px-3 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Chấm công
                </th>
                <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Kế toán nhập
                </th>
                <th className="px-2 sm:px-3 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Thiếu/Thừa
                </th>
                <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap w-40">
                  Ứng tiền khách 150k
                </th>
                <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Chi phí
                </th>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap w-28">
                  Note
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan={13}
                    className="px-2 sm:px-3 py-6 text-center text-xs text-slate-500"
                  >
                    Đang tải dữ liệu...
                  </td>
                </tr>
              ) : (
                <>
                  {localRows.map((row, idx) => {
                  const rowKey = row.isNew ? row.tempId || "new" : row.rowIndex;
                  const isEditingRow = editingCell?.rowKey === rowKey;
                  const editingField = editingCell?.field;

                  // Tìm dòng đầu tiên và cuối cùng có cùng ngày với activeDate
                  const isActiveDate = activeDate && row.ngay && row.ngay.trim() === activeDate.trim();
                  let isFirstRowOfDate = false;
                  let isLastRowOfDate = false;
                  
                  if (isActiveDate && activeDate) {
                    // Tìm tất cả các index có cùng ngày
                    const sameDateIndices = [];
                    localRows.forEach((r, i) => {
                      if (r.ngay && r.ngay.trim() === activeDate.trim()) {
                        sameDateIndices.push(i);
                      }
                    });
                    
                    if (sameDateIndices.length > 0) {
                      const firstIndex = sameDateIndices[0];
                      const lastIndex = sameDateIndices[sameDateIndices.length - 1];
                      
                      isFirstRowOfDate = idx === firstIndex;
                      isLastRowOfDate = idx === lastIndex;
                    }
                  }

                  return (
                    <tr
                      id={row.isNew ? `row-${row.tempId || `new-${idx}`}` : `row-${row.rowIndex}`}
                      key={row.isNew ? row.tempId || `new-${idx}` : row.rowIndex}
                      className={`${(() => {
                        // Kiểm tra xem row có cùng ngày với activeDate không
                        if (isActiveDate) return "bg-blue-100/0"; // Màu xanh rất nhạt (opacity 20%)
                        
                        if (row.isNew) return "bg-blue-50";
                        const hasDoanhThu = row.doanhThu && row.doanhThu > 0;
                        const hasKeToan = row.keToanChuyen !== '' && row.keToanChuyen !== null && row.keToanChuyen !== undefined;
                        if (hasDoanhThu && !hasKeToan) return "bg-amber-50";
                        return "bg-white";
                      })()} hover:bg-slate-100 transition-colors ${
                        row.isNew ? "" : ""
                      } ${
                        saving[`delete-${row.rowIndex}`] ? "opacity-50" : ""
                      } ${
                        isFirstRowOfDate ? "border-t-2 border-blue-500" : ""
                      } ${
                        isLastRowOfDate ? "border-b-2 border-blue-500" : ""
                      }`}
                      style={{
                        ...(isFirstRowOfDate ? { borderTop: '2px solid rgb(59 130 246)' } : {}),
                        ...(isLastRowOfDate ? { borderBottom: '2px solid rgb(59 130 246)' } : {}),
                      }}
                    >
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1 relative insert-menu-container">
                          {canEdit && (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInsertMenuOpen(
                                    insertMenuOpen === idx ? null : idx
                                  );
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs px-1 sm:px-2 py-1 rounded hover:bg-blue-50"
                                title="Chèn hàng"
                              >
                                ➕
                              </button>
                              {insertMenuOpen === idx && (
                                <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-20 min-w-[140px] sm:min-w-[160px]">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInsertRow(idx, "above");
                                      setInsertMenuOpen(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                  >
                                    <span>⬆️</span>
                                    <span>Chèn hàng phía trên</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInsertRow(idx, "below");
                                      setInsertMenuOpen(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-t border-slate-100"
                                  >
                                    <span>⬇️</span>
                                    <span>Chèn hàng phía dưới</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {canEdit && row.isNew && (
                            <>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await handleSaveNewRow(row);
                                }}
                                disabled={saving.new}
                                className="text-emerald-700 hover:text-emerald-900 text-xs px-1 sm:px-2 py-1 rounded hover:bg-emerald-50 disabled:opacity-50 whitespace-nowrap"
                                title="Lưu hàng đang thêm"
                              >
                                Lưu
                              </button>
                              {row.tempId && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTempRow(row.tempId);
                                  }}
                                  className="text-slate-600 hover:text-slate-800 text-xs px-1 sm:px-2 py-1 rounded hover:bg-slate-100 whitespace-nowrap"
                                  title="Hủy hàng đang thêm"
                                >
                                  Hủy
                                </button>
                              )}
                            </>
                          )}

                          {canEdit && row.rowIndex && (
                            <button
                              onClick={() => handleDeleteRow(row.rowIndex)}
                              disabled={saving[`delete-${row.rowIndex}`]}
                              className="text-red-600 hover:text-red-800 text-xs px-1 sm:px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50"
                              title="Xóa hàng"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>

                      <EditableCell
                        value={row.ngay}
                        onSave={(v) => handleCellSave(rowKey, "ngay", v)}
                        isEditing={isEditingRow && editingField === "ngay"}
                        onStartEdit={() => handleCellEdit(rowKey, "ngay")}
                        onCancelEdit={() => setEditingCell(null)}
                        formatValue={(v) => v || ""}
                        disabled={isReadOnly}
                      />

                      <EditableCell
                        value={row.laiXe}
                        onSave={(v) => handleCellSave(rowKey, "laiXe", v)}
                        options={options.laiXe}
                        isEditing={isEditingRow && editingField === "laiXe"}
                        onStartEdit={() => handleCellEdit(rowKey, "laiXe")}
                        onCancelEdit={() => setEditingCell(null)}
                        formatValue={(v) => v || ""}
                        disabled={isReadOnly}
                      />

                      <EditableCell
                        value={row.soXe}
                        onSave={(v) => handleCellSave(rowKey, "soXe", v)}
                        options={options.soXe}
                        isEditing={isEditingRow && editingField === "soXe"}
                        onStartEdit={() => handleCellEdit(rowKey, "soXe")}
                        onCancelEdit={() => setEditingCell(null)}
                        formatValue={(v) => v || ""}
                        disabled={isReadOnly}
                      />

                      <EditableCell
                        value={row.dieuPhoi}
                        onSave={(v) => handleCellSave(rowKey, "dieuPhoi", v)}
                        options={options.dieuPhoi}
                        isEditing={isEditingRow && editingField === "dieuPhoi"}
                        onStartEdit={() => handleCellEdit(rowKey, "dieuPhoi")}
                        onCancelEdit={() => setEditingCell(null)}
                        formatValue={(v) => v || ""}
                        disabled={isReadOnly}
                      />

                      <EditableCell
                        value={row.doanhThu}
                        onSave={(v) => handleCellSave(rowKey, "doanhThu", v)}
                        isEditing={
                          isEditingRow && editingField === "doanhThu"
                        }
                        onStartEdit={() => handleCellEdit(rowKey, "doanhThu")}
                        onCancelEdit={() => setEditingCell(null)}
                        align="right"
                        disabled={isReadOnly}
                        formatValue={(v) => {
                          if (!v && v !== 0) return "";
                          const num =
                            typeof v === "string"
                              ? Number(v.replace(/[^\d-]/g, ""))
                              : v;
                          return num.toLocaleString("vi-VN") + " đ";
                        }}
                        parseValue={(v) => {
                          if (typeof v === "string") {
                            return Number(v.replace(/[^\d-]/g, "")) || 0;
                          }
                          return v || 0;
                        }}
                      />

                      <EditableCell
                        value={row.diemThuc}
                        onSave={(v) => handleCellSave(rowKey, "diemThuc", v)}
                        options={options.diemThuc}
                        isEditing={
                          isEditingRow && editingField === "diemThuc"
                        }
                        onStartEdit={() => handleCellEdit(rowKey, "diemThuc")}
                        onCancelEdit={() => setEditingCell(null)}
                        align="center"
                        formatValue={(v) => v ?? ""}
                        disabled={isReadOnly}
                      />

                      <EditableCell
                        value={row.chanCong}
                        onSave={(v) => handleCellSave(rowKey, "chanCong", v)}
                        options={options.chanCong}
                        isEditing={
                          isEditingRow && editingField === "chanCong"
                        }
                        onStartEdit={() => handleCellEdit(rowKey, "chanCong")}
                        onCancelEdit={() => setEditingCell(null)}
                        align="center"
                        formatValue={(v) => v ?? 0}
                        disabled={isReadOnly}
                      />

                      <EditableCell
                        value={row.keToanChuyen}
                        onSave={(v) =>
                          handleCellSave(rowKey, "keToanChuyen", v)
                        }
                        isEditing={
                          isEditingRow && editingField === "keToanChuyen"
                        }
                        onStartEdit={() =>
                          handleCellEdit(rowKey, "keToanChuyen")
                        }
                        onCancelEdit={() => setEditingCell(null)}
                        align="right"
                        disabled={isReadOnly}
                        formatValue={(v) => {
                          if (v === "" || v === null || v === undefined)
                            return "";
                          const num =
                            typeof v === "string"
                              ? Number(v.replace(/[^\d-]/g, ""))
                              : v;
                          if (Number.isNaN(num)) return v;
                          return num.toLocaleString("vi-VN") + " đ";
                        }}
                        parseValue={(v) => {
                          if (v === "" || v === null || v === undefined)
                            return "";
                          if (typeof v === "string") {
                            const cleaned = v.replace(/[^\d-]/g, "");
                            if (!cleaned) return "";
                            return Number(cleaned) || "";
                          }
                          return v;
                        }}
                      />

                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-center">
                        <span
                          className={
                            row.doiChieuNote?.toLowerCase().includes("ok")
                              ? "text-emerald-700 font-semibold"
                              : row.doiChieuNote
                                  ?.toLowerCase()
                                  .includes("trước")
                              ? "text-blue-600 font-semibold"
                              : row.doiChieuNote
                                  ?.toLowerCase()
                                  .includes("thiếu")
                              ? "text-rose-700 font-semibold"
                              : row.doiChieuNote
                                  ?.toLowerCase()
                                  .includes("thừa")
                              ? "text-amber-700 font-semibold"
                              : row.doiChieuNote
                              ? "text-slate-700"
                              : "text-slate-400"
                          }
                          title={row.doiChieuNote || ""}
                        >
                          {row.doiChieuNote || ""}
                        </span>
                      </td>

                      <EditableCell
                        value={row.ungTienKhach150k}
                        onSave={(v) =>
                          handleCellSave(rowKey, "ungTienKhach150k", v)
                        }
                        isEditing={isEditingRow && editingField === "ungTienKhach150k"}
                        onStartEdit={() =>
                          handleCellEdit(rowKey, "ungTienKhach150k")
                        }
                        onCancelEdit={() => setEditingCell(null)}
                        className="max-w-[8rem] truncate align-top text-right"
                        formatValue={(v) => v ?? ""}
                        disabled={isReadOnly}
                      />

                      <EditableCell
                        value={row.chiPhi}
                        onSave={(v) => handleCellSave(rowKey, "chiPhi", v)}
                        isEditing={isEditingRow && editingField === "chiPhi"}
                        onStartEdit={() => handleCellEdit(rowKey, "chiPhi")}
                        onCancelEdit={() => setEditingCell(null)}
                        align="right"
                        disabled={isReadOnly}
                        formatValue={(v) => {
                          if (v === "" || v === null || v === undefined) return "";
                          const num =
                            typeof v === "string"
                              ? Number(v.replace(/[^\d-]/g, ""))
                              : v;
                          if (Number.isNaN(num)) return v;
                          return num.toLocaleString("vi-VN") + " đ";
                        }}
                        parseValue={(v) => {
                          if (v === "" || v === null || v === undefined) return "";
                          if (typeof v === "string") {
                            const cleaned = v.replace(/[^\d-]/g, "");
                            if (!cleaned) return "";
                            return Number(cleaned) || "";
                          }
                          return v;
                        }}
                      />

                      <EditableCell
                        value={row.noteNhap}
                        onSave={(v) => handleCellSave(rowKey, "noteNhap", v)}
                        isEditing={
                          isEditingRow && editingField === "noteNhap"
                        }
                        onStartEdit={() => handleCellEdit(rowKey, "noteNhap")}
                        onCancelEdit={() => setEditingCell(null)}
                        className="max-w-[8rem] truncate align-top"
                        formatValue={(v) => v || ""}
                        disabled={isReadOnly}
                      />
                    </tr>
                  );
                })}

                  {localRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={13}
                        className="px-2 sm:px-3 py-6 text-center text-xs text-slate-500"
                      >
                        Chưa có dữ liệu.
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RevenueTable;

